/**
 * کلیدهای تماسِ خودِ کاربر — «دوستانم از چه راهی می‌تونن پیدام کنن».
 *
 * دو تا صادرکننده داره و هر دو یه جدول رو می‌خونن (`user_contact_keys`):
 * `phoneRepo` برای شماره‌ها (ثبت، تایید، پیش‌فرض، حذف) و `contactKeyRepo`
 * برای ایمیلِ حساب که خودکار ثبت می‌شه (مایگریشن 0073).
 *
 * چرا توی core و نه کنارِ contactsRepo (که مالِ موبایله): از مایگریشن 0060
 * ثبتِ شماره کدِ پیامکی می‌خواد، و اون جریان روی وب هم هست. contactsRepo
 * عمداً موبایل‌مونده چون به دفترچه‌ی تلفنِ گوشی وابسته‌ست؛ «شماره‌ی خودم»
 * هیچ ربطی به دفترچه نداره. contactsRepo همین‌ها رو re-export می‌کنه تا
 * صفحه‌های موجودِ موبایل دست‌نخورده بمونن.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { normalizePhone, phoneKey } from '../lib/contacts';
import { callingCodeForCountry } from '../lib/callingCodes';
import { reportError } from '../lib/report';
import type { MyPhone, StartVerifyResult, VerifyPhoneResult } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type { StartVerifyResult, VerifyPhoneResult };

/**
 * روی ابر یعنی فرستنده‌ی پیامک هنوز مستقر نشده
 * (`node scripts/deploy-sms.mjs`). صفحه‌ها ازش استفاده می‌کنن تا به‌جای
 * «خطا — دوباره امتحان کن» بگن چرا هیچ کدی نمی‌رسه.
 */
export const isSmsUnconfiguredError = (e: unknown): boolean =>
  typeof (e as { message?: unknown })?.message === 'string'
  && (e as { message: string }).message.includes('sms_not_configured');

/**
 * کدِ کشور به‌ندرت لازم میشه ولی بک‌فیلِ سینک ممکنه ده‌ها بار پشتِ هم
 * بخوادش — بدون کش یعنی دو کوئری برای هر ردیف. شهرِ پروفایل وسطِ نشست
 * عوض بشه تا اجرای بعدی همین کش می‌مونه؛ برای یه پیش‌فرضِ نرمال‌سازی
 * قابل‌قبوله.
 */
let ccCache: { uid: string; cc: string } | null = null;

export const phoneRepo = {
  /**
   * کد تلفنِ کشورِ کاربر («98»، «1»، …)، از شهرِ پروفایلش (WHOSUP-162).
   *
   * برای این که شماره‌ای که بدونِ کد کشور وارد میشه — از دفترچه‌ی گوشی یا
   * فرمِ دستی — کد کشورِ *خودِ کاربر* رو بگیره، نه «1»ِ هاردکد. هر شکستی
   * این‌جا بی‌صدا به «1» برمی‌گرده: نرمال‌سازیِ ناقص بهتر از شکستنِ ذخیره‌ست.
   */
  async myCallingCode(): Promise<string> {
    if (isDemoMode) return '1';
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return '1';
      if (ccCache?.uid === auth.user.id) return ccCache.cc;
      const { data: prof } = await supabase
        .from('profiles').select('city_id').eq('id', auth.user.id).maybeSingle();
      if (!prof?.city_id) return '1';
      const { data: city } = await supabase
        .from('cities').select('country_code').eq('id', prof.city_id).maybeSingle();
      const cc = callingCodeForCountry(city?.country_code) ?? '1';
      ccCache = { uid: auth.user.id, cc };
      return cc;
    } catch (e) {
      reportError('phoneRepo.myCallingCode', e);
      return '1';
    }
  },

  /**
   * همه‌ی شماره‌های ثبت‌شده‌ی خودم، پیش‌فرض اول.
   *
   * از مایگریشن 0060 هر ردیفِ این‌جا تاییدشده‌ست — ثبتِ نشده‌ای وجود نداره،
   * پس نشانِ «تاییدشده» هم لازم نیست.
   */
  async mine(): Promise<MyPhone[]> {
    if (isDemoMode) return demoStore.contacts.myPhones();
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return [];
    const { data, error } = await supabase
      .from('user_contact_keys')
      .select('value, is_default')
      .eq('user_id', auth.user.id).eq('kind', 'phone')
      .order('is_default', { ascending: false })
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ value: r.value, isDefault: !!r.is_default }));
  },

  /**
   * شماره‌ی پیش‌فرضم.
   *
   * بدون این، فیلدِ «شماره‌ی من» همیشه خالی بالا می‌اومد و کاربر فکر می‌کرد
   * ثبت نشده — دقیقاً همون چیزی که موقع ثبت‌نام وارد کرده بود.
   */
  async default(): Promise<string | null> {
    if (isDemoMode) return demoStore.contacts.myPhone();
    const phones = await phoneRepo.mine();
    return phones.find((p) => p.isDefault)?.value ?? phones[0]?.value ?? null;
  },

  /**
   * کدِ شش‌رقمی رو به همین شماره پیامک می‌کنه.
   *
   * خودِ کد هیچ‌وقت به کلاینت نمی‌رسه — تنها راهِ دیدنش پیامکه، که کلِ نکته‌ی
   * تاییده.
   */
  async startVerification(phone: string): Promise<StartVerifyResult> {
    if (isDemoMode) return demoStore.contacts.startPhoneVerification(phone);
    // شماره همیشه بین‌المللی به سرور می‌ره؛ وگرنه `phone_e164` سمتِ SQL با
    // پیش‌فرضِ «1» خودش حدس می‌زد و کد به شماره‌ی غلط پیامک می‌شد.
    const e164 = normalizePhone(phone, await phoneRepo.myCallingCode()) ?? phone;
    const { data, error } = await supabase.rpc('start_phone_verification', { p_phone: e164 });
    if (error) throw error;
    const r = data as any;
    if (r.status === 'throttled') return { status: 'throttled', retryAfter: Number(r.retry_after) };
    if (r.status === 'sent') return { status: 'sent', expiresAt: String(r.expires_at) };
    return { status: r.status };
  },

  /**
   * کد رو می‌سنجه و اگه درست بود شماره رو ثبت می‌کنه.
   *
   * `taken` یعنی شماره مالِ حسابِ دیگه‌ای بود و همین حالا منتقل شد — مهلتِ
   * سه‌روزه‌ی 0019 فقط برای ثبتِ **بدونِ اثبات** بود و دیگه اجرا نمیشه.
   */
  async verify(phone: string, code: string): Promise<VerifyPhoneResult> {
    if (isDemoMode) return demoStore.contacts.verifyPhone(phone, code);
    // همون نرمال‌سازیِ startVerification، تا هر دو RPC یک شماره ببینن.
    const e164 = normalizePhone(phone, await phoneRepo.myCallingCode()) ?? phone;
    const { data, error } = await supabase.rpc('verify_phone', { p_phone: e164, p_code: code });
    if (error) throw error;
    const r = data as any;
    if (r.status === 'wrong') return { status: 'wrong', left: Number(r.left) };
    return { status: r.status };
  },

  /** کدوم شماره «شماره‌ی من» باشه. باید از قبل مالِ خودم باشه. */
  async setDefault(phone: string): Promise<void> {
    if (isDemoMode) return demoStore.contacts.setDefaultPhone(phone);
    const { error } = await supabase.rpc('set_default_phone', { p_phone: phone });
    if (error) throw error;
  },

  /**
   * شماره‌ای که دیگه مالِ من نیست.
   *
   * تا وقتی ثبت‌شده‌ست، هیچ‌کس دیگه‌ای نمی‌تونه برش داره — پس پس دادنش
   * لازمه، نه یه امکانِ لوکس. اگه پیش‌فرض بوده، سرور قدیمی‌ترین شماره‌ی
   * باقی‌مونده رو پیش‌فرض می‌کنه (تریگرِ مایگریشن 0048).
   */
  async remove(phone: string): Promise<void> {
    if (isDemoMode) return demoStore.contacts.removePhone(phone);
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { error } = await supabase
      .from('user_contact_keys')
      .delete()
      .eq('user_id', auth.user.id).eq('kind', 'phone')
      .eq('value', phoneKey(phone) ?? phone);
    if (error) throw error;
  },
};

export const contactKeyRepo = {
  /**
   * ایمیلی که به‌عنوانِ کلیدِ تماسِ من ثبت شده — یا `null` اگه هیچی ثبت نشده.
   *
   * چرا خودِ مقدار برمی‌گرده و نه یه بله/خیر: این **همون رشته‌ایه که تطبیق
   * روش انجام می‌شه** — کوچک‌شده و نرمال‌شده (`email_key`). ممکنه با چیزی
   * که کاربر توی حسابش می‌بینه فرق داشته باشه، و دقیقاً همون فرق چیزیه که
   * وقتی «چرا دوستم پیدام نکرد» پیش می‌آد باید بشه دیدش.
   *
   * سیاستِ `uck_sel` فقط ردیف‌های خودِ کاربر رو می‌ده، پس این کوئری هیچ‌وقت
   * کلیدِ کسِ دیگه‌ای رو برنمی‌گردونه.
   */
  async myEmail(): Promise<string | null> {
    if (isDemoMode) return demoStore.contacts.myEmailKey();
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return null;
    const { data, error } = await supabase
      .from('user_contact_keys')
      .select('value')
      .eq('user_id', auth.user.id).eq('kind', 'email')
      .order('created_at')
      .limit(1);
    if (error) throw error;
    return (data ?? [])[0]?.value ?? null;
  },
};
